import { registrationPriceFromBoards } from "../constants/tournamentPricing";
import type { Participation } from "../types/Participation";
import type { Tableau } from "../types/Tableau";

const PAID_STATUSES = new Set(["admin", "registration", "offert"]);

export function isParticipationPaid(
  paymentStatus: Participation["paymentStatus"]
): boolean {
  return paymentStatus != null && PAID_STATUSES.has(paymentStatus);
}

/** Libellé lisible : « A — Nom » si code ≠ label, sinon label ou code. */
function formatBoardDisplayName(
  board: Pick<Tableau, "name" | "label"> | null | undefined,
  fallbackId?: unknown
): string {
  const code = typeof board?.name === "string" ? board.name.trim() : "";
  const label = typeof board?.label === "string" ? board.label.trim() : "";
  if (code && label && code !== label) {
    return `${code} — ${label}`;
  }
  return label || code || String(fallbackId ?? "Tableau");
}

export type PlayerRegistrationBalance = {
  paidParticipations: Participation[];
  unpaidParticipations: Participation[];
  paidBoards: Array<Pick<Tableau, "offert" | "fullPrice" | "name"> | null | undefined>;
  unpaidBoardNames: string[];
  paidBoardNames: string[];
  totalEuros: number;
  paidEuros: number;
  dueEuros: number;
};

/** Solde d'inscription : prix(tous) − prix(payés), grille par paliers. */
export function computePlayerRegistrationBalance(
  participations: Participation[],
  pricingTag?: string | null
): PlayerRegistrationBalance {
  const paidParticipations = participations.filter((p) =>
    isParticipationPaid(p.paymentStatus)
  );
  const unpaidParticipations = participations.filter(
    (p) => !isParticipationPaid(p.paymentStatus)
  );

  const allBoards = participations.map((p) => p.tableau);
  const paidBoards = paidParticipations.map((p) => p.tableau);

  const totalEuros = registrationPriceFromBoards(allBoards, pricingTag);
  const paidEuros = registrationPriceFromBoards(paidBoards, pricingTag);

  return {
    paidParticipations,
    unpaidParticipations,
    paidBoards,
    unpaidBoardNames: unpaidParticipations.map((p) =>
      formatBoardDisplayName(p.tableau, p.tableauId)
    ),
    paidBoardNames: paidParticipations.map((p) =>
      formatBoardDisplayName(p.tableau, p.tableauId)
    ),
    totalEuros,
    paidEuros,
    dueEuros: totalEuros - paidEuros,
  };
}
